import type { RestRequest } from 'msw'
import type { TEntityDataForPost } from '~/types/models'

/**
 * Read the posted entity from either a JSON body or a multipart/form-data
 * upload. httpPost (via buildPostBody) sends file uploads as multipart: the
 * File(s) as binary part(s) plus the remaining fields as a single JSON
 * `payload` part. MSW v1 parses multipart bodies into a record of
 * field -> string | File (accessible via req.body), so we rebuild the entity
 * from `payload` and derive the file metadata (filename/filesize/filetype) the
 * real server would persist. The binary itself is not stored in the mock list
 * data.
 *
 * Extracted from mockDataCommon so it can be unit-tested without dragging in the
 * localforage-backed mock storage layer.
 */
export async function parsePostedEntity(
  req: RestRequest
): Promise<TEntityDataForPost> {
  const contentType = req.headers.get('content-type') || ''
  if (!contentType.includes('multipart/form-data')) {
    return await req.json()
  }
  const body = req.body as Record<string, string | File>
  const entity: Record<string, any> = JSON.parse(String(body.payload ?? '{}'))
  for (const value of Object.values(body)) {
    if (value instanceof File) {
      entity.filename = value.name
      entity.filesize = value.size
      entity.filetype = value.type
      // Mock stand-in for the server-generated download URL. The mock doesn't
      // persist the binary, so this is a placeholder that simply enables the
      // View and Download actions; the real server returns an actual file URL.
      //
      // ⚠ THE `?sig=` IS LOAD-BEARING, not decoration. The real path is
      // `{base}/files/{id}?sig={hmac}` (document_url.file_url_path), and the
      // Download item appends `dl=1` to it. A stand-in with no query string
      // would make dev and e2e exercise a URL shape production never produces --
      // and the one thing that shape changes is exactly the separator the new
      // parameter needs. `mock` is not a real signature and nothing verifies it;
      // only the SHAPE matters here.
      entity.fileUrlPath = `/mock-files/${entity.id ?? 'unknown'}/${
        value.name
      }?sig=mock`
    }
  }
  return entity as TEntityDataForPost
}
